'use client'

import { Card, CardContent, CardHeader, CardTitle } from '@alphabait/ui' // card'
import { useTranslation } from 'react-i18next'
import { useTsrClient } from '../../end-users/tsr'
import { AlertCircle } from 'lucide-react'

interface MetricCardProps {
  title: string
  subtitle: string
  value: string
  details?: string
  isLoading: boolean
  error?: boolean
}

function MetricCard({ title, value, isLoading, error }: MetricCardProps) {
  const { t } = useTranslation()

  return (
    <Card className="bg-light-blue-100 border-0 rounded-[16px] items-stretch flex flex-col h-full px-3 py-2">
      <CardHeader className="h-full py-4">
        <CardTitle className="text-lg font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col h-10 align-middle justify-center">
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">
              {t('dashboard.loading')}
            </span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <span className="text-sm text-destructive">
              {t('dashboard.error')}
            </span>
          </div>
        ) : (
          <div>
            <div className="text-2xl font-bold">{value}</div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface KeyMetricsProps {
  periodStart: string
  periodEnd: string
  customerId?: string
  campaignIds?: string[]
  campaignGroupId?: string
}

export function KeyMetrics({
  periodStart,
  periodEnd,
  customerId,
  campaignIds,
  campaignGroupId,
}: KeyMetricsProps) {
  const { t } = useTranslation()
  const tsr = useTsrClient()

  // Convert dates to timestamps for the API
  const startTimestamp = new Date(periodStart).getTime() / 1000
  const endTimestamp = new Date(periodEnd).getTime() / 1000

  // 1. Reach Rate - Users who reached the campaign
  const {
    data: reachData,
    isPending: isReachLoading,
    error: reachError,
  } = tsr.dashboard.getReachedRate.useQuery({
    queryKey: [
      'reachedRate',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
    ],
    queryData: {
      query: {
        customerId,
        campaignIds,
        campaignGroupId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      },
    },
  })

  // 2. Average Happiness Score
  const {
    data: happinessData,
    isPending: isHappinessLoading,
    error: happinessError,
  } = tsr.dashboard.getAverageHappinessCheck.useQuery({
    queryKey: [
      'averageHappinessCheck',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
    ],
    queryData: {
      query: {
        customerId,
        campaignIds,
        campaignGroupId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      },
    },
  })

  // 3. Incentive Claim Rate - Users who completed questionnaires
  const {
    data: incentiveData,
    isPending: isIncentiveLoading,
    error: incentiveError,
  } = tsr.dashboard.getIncentiveClaimRate.useQuery({
    queryKey: [
      'incentiveClaimRate',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
    ],
    queryData: {
      query: {
        customerId,
        campaignIds,
        campaignGroupId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      },
    },
  })

  // 4. Data Enrichment Score - Questionnaire completion rate
  const {
    data: enrichmentData,
    isPending: isEnrichmentLoading,
    error: enrichmentError,
  } = tsr.dashboard.getQuestionnaireCompletionRate.useQuery({
    queryKey: [
      'questionnaireCompletionRate',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
    ],
    queryData: {
      query: {
        customerId,
        campaignIds,
        campaignGroupId,
        startDate: startTimestamp,
        endDate: endTimestamp,
      },
    },
  })

  // Helper function to get metric card props
  const getReachRateProps = (): Omit<
    MetricCardProps,
    'title' | 'subtitle'
  > => ({
    value:
      reachData?.body && reachData.body.reachedPercentage > 0
        ? `${reachData.body.reachedPercentage}%`
        : t('dashboard.no_data_available'),
    details: reachData?.body
      ? `${reachData.body.reachedAudience.toLocaleString()} / ${reachData.body.totalAudience.toLocaleString()} ${t(
          'dashboard.users',
        )}`
      : undefined,
    isLoading: isReachLoading,
    error: !!reachError,
  })

  const getEnrichmentProps = (): Omit<
    MetricCardProps,
    'title' | 'subtitle'
  > => ({
    value:
      enrichmentData?.body && enrichmentData.body.completionRate > 0
        ? `${enrichmentData.body.completionRate}%`
        : t('dashboard.no_data_available'),
    details: enrichmentData?.body
      ? `${enrichmentData.body.totalAnsweredQuestions.toLocaleString()} / ${enrichmentData.body.totalQuestions.toLocaleString()} ${t(
          'dashboard.questions',
        )}`
      : undefined,
    isLoading: isEnrichmentLoading,
    error: !!enrichmentError,
  })

  const getIncentiveProps = (): Omit<
    MetricCardProps,
    'title' | 'subtitle'
  > => ({
    value:
      incentiveData?.body && incentiveData.body.claimRate > 0
        ? `${incentiveData.body.claimRate}%`
        : t('dashboard.no_data_available'),
    details: incentiveData?.body
      ? `${incentiveData.body.claimedIncentives.toLocaleString()} / ${incentiveData.body.totalAudience.toLocaleString()} ${t(
          'dashboard.users',
        )}`
      : undefined,
    isLoading: isIncentiveLoading,
    error: !!incentiveError,
  })

  const getHappinessProps = (): Omit<
    MetricCardProps,
    'title' | 'subtitle'
  > => ({
    value:
      happinessData?.body && happinessData.body.averageHappiness > 0
        ? `${happinessData.body.averageHappiness.toFixed(1)}/5`
        : t('dashboard.no_ratings_yet'),
    details:
      happinessData?.body && happinessData.body.averageHappiness > 0
        ? `${happinessData.body.totalResponses.toLocaleString()} ${t(
            'dashboard.responses',
          )}`
        : undefined,
    isLoading: isHappinessLoading,
    error: !!happinessError,
  })

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
      <MetricCard
        title={t('dashboard.total_reach_rate')}
        subtitle={t('dashboard.reach_rate_subtitle')}
        {...getReachRateProps()}
      />

      <MetricCard
        title={t('dashboard.data_enrichment_score')}
        subtitle={t('dashboard.enrichment_subtitle')}
        {...getEnrichmentProps()}
      />

      <MetricCard
        title={t('dashboard.incentive_claim_rate')}
        subtitle={t('dashboard.incentive_subtitle')}
        {...getIncentiveProps()}
      />

      <MetricCard
        title={t('dashboard.average_happiness_score')}
        subtitle={t('dashboard.happiness_subtitle')}
        {...getHappinessProps()}
      />
    </div>
  )
}
